import { useEffect, useState } from 'react';
import { Money } from './money';
import Icon from './Icon';
import { dropAuction, useAuctions, type AuctionSeat } from './auctions';
import { serverNow } from './realtime';

/**
 * Полоска торгов над нижним меню.
 *
 * Зачем она вообще: в аукционе решение принимается за секунды, а человек
 * в это время смотрит другой экран или другую заявку. Уведомление в
 * колокольчике он увидит через час, когда торги давно кончились. Полоска
 * стоит там, где палец и где нижняя панель, — в поле зрения, но не
 * поверх содержимого.
 *
 * Показывается только участнику этих торгов: чужой обратный отсчёт —
 * шум, а не информация. Одна полоска на всё приложение, самая срочная;
 * остальные свёрнуты в счётчик рядом.
 *
 * Три состояния, и в них вся польза:
 *  — веду: спокойное, ничего делать не надо;
 *  — перебили: заметное, с кнопкой, которая сразу открывает форму;
 *  — закрылось: исход, и через двадцать секунд полоска уходит сама.
 *
 * Обратный отсчёт идёт от `serverNow`, а не от локальных часов. Срок
 * торгов неподвижен, но решает по нему сервер, а часы на телефоне врут
 * на минуты — и разойдясь с ним, полоска либо покажет ноль, когда ставки
 * ещё принимают, либо оставит форму открытой, когда уже нет. Второе хуже:
 * человек набирает ставку, которую отклонят.
 */

/** Ниже этого порога торги считаются горящими. */
const URGENT_MS = 60_000;

function left(seat: AuctionSeat, now: number): number {
  return seat.bidEndsAt === null ? Infinity : Math.max(0, seat.bidEndsAt - now);
}

function clock(ms: number): string {
  if (!Number.isFinite(ms)) return 'без срока';

  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  // Часы показываем, только когда они есть: «0:04:12» читается медленнее,
  // чем «4:12», а под конец торгов важна каждая доля секунды внимания.
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function AuctionBar({ onBid }: { onBid: (cargoId: string) => void }) {
  const seats = useAuctions();
  const [now, setNow] = useState(() => serverNow());
  const [folded, setFolded] = useState(false);

  /*
   * Тик живёт здесь, а не в общем состоянии приложения: секунда не должна
   * перерисовывать ленту. И он не заводится вовсе, когда торгов нет.
   */
  useEffect(() => {
    if (seats.length === 0) return;
    const t = window.setInterval(() => setNow(serverNow()), 1000);
    return () => window.clearInterval(t);
  }, [seats.length]);

  if (seats.length === 0) return null;

  const seat = seats[0]!;
  const rest = seats.length - 1;
  const ms = left(seat, now);
  const urgent = !seat.closed && ms <= URGENT_MS;

  const tone = seat.closed
    ? seat.closed.won
      ? 'won'
      : 'lost'
    : seat.leading
      ? 'leading'
      : 'outbid';

  if (folded) {
    return (
      <button
        type="button"
        className={`auctionbar auctionbar--folded auctionbar--${tone}`}
        onClick={() => setFolded(false)}
        title="Развернуть торги"
      >
        <Icon name="clock" size={14} />
        <span className="tabular">{clock(ms)}</span>
        {rest > 0 && <span className="auctionbar__more">+{rest}</span>}
      </button>
    );
  }

  return (
    <div className={`auctionbar auctionbar--${tone}`} role="status" aria-live="polite">
      <div className="auctionbar__body">
        <div className="auctionbar__line">
          <strong className="auctionbar__title">{seat.title}</strong>
          {rest > 0 && (
            <span className="auctionbar__more" title="Ещё торги с вашей ставкой">
              ещё {rest}
            </span>
          )}
        </div>

        <div className="auctionbar__line auctionbar__state">
          {seat.closed ? (
            seat.closed.won ? (
              <span>
                Вы выиграли
                {seat.closed.winningBid !== null && (
                  <>
                    {' — '}
                    <Money frozen value={seat.closed.winningBid} currency={seat.currency} />
                  </>
                )}
              </span>
            ) : (
              <span>Торги завершены, груз ушёл</span>
            )
          ) : (
            <>
              <span className={urgent ? 'auctionbar__clock is-urgent' : 'auctionbar__clock'}>
                <Icon name="clock" size={13} />
                <span className="tabular">{clock(ms)}</span>
              </span>
              <span className="auctionbar__bid">
                {seat.leading ? 'ваша ставка лучшая' : 'вашу ставку перебили'}
                {seat.bestBid !== null && (
                  <>
                    {' · '}
                    <Money value={seat.bestBid} currency={seat.currency} />
                  </>
                )}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Действие ровно одно и ровно то, которое нужно сейчас: проигравшему
          перебить, победителю открыть рейс. Второй кнопки тут нет — на
          полоску в шесть сантиметров выбор не помещается.

          Закрытые торги после нажатия убираются сразу: человек на них уже
          ответил, и держать плашку ещё двадцать секунд — показывать
          уведомление о том, что он в эту секунду и открыл. */}
      {seat.closed ? (
        seat.closed.won && seat.closed.orderId ? (
          <button
            type="button"
            className="auctionbar__act"
            onClick={() => {
              onBid(seat.cargoId);
              dropAuction(seat.cargoId);
            }}
          >
            Открыть
          </button>
        ) : null
      ) : (
        !seat.leading &&
        seat.nextBid !== null && (
          <button type="button" className="auctionbar__act" onClick={() => onBid(seat.cargoId)}>
            Перебить
          </button>
        )
      )}

      {/* У закрытых торгов — «убрать», у живых — «свернуть».
          Итог это уведомление, и закрыть его надо уметь сразу, не дожидаясь,
          пока оно уйдёт само; живые торги убирать нельзя — потеряется
          отсчёт, ради которого полоска и висит. */}
      {seat.closed ? (
        <button
          type="button"
          className="auctionbar__fold"
          onClick={() => dropAuction(seat.cargoId)}
          aria-label="Убрать"
          title="Убрать"
        >
          <Icon name="close" size={16} />
        </button>
      ) : (
        <button
          type="button"
          className="auctionbar__fold"
          onClick={() => setFolded(true)}
          aria-label="Свернуть"
          title="Свернуть до таймера"
        >
          <Icon name="down" size={16} />
        </button>
      )}
    </div>
  );
}
